import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState, PageTransition } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@tendly/shared";
import { readAccountsStateSync } from "@/lib/accounts";
import { resolveActiveAccountView } from "@/lib/active-account";
import { activeTabAccountId } from "@/lib/tab-account";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

/**
 * Auth route shell.
 *
 * #13 de-cloned Tendly's auth: there is no longer a shared `AuthLayout`/`AuthCard`
 * frame here. Each auth screen renders its own `AuthScene` (Tendly's
 * service-record shell with the scroll fix baked in), so the route only owns the
 * redirect-if-authenticated guard and the page transition. This keeps the form
 * inside its own scroll container instead of the old `AppBackground`-clipped
 * layout.
 */
function AuthLayoutRoute() {
  const location = useLocation();
  appState.markAsReady();

  return (
    <PageTransition routeKey={location().pathname}>
      <Outlet />
    </PageTransition>
  );
}

const fetchSession = async () =>
  api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

/**
 * Writes the `get-session` response into the machine and cache.
 *
 * It is called ALWAYS, even when `beforeLoad` already gave up due to timeout:
 * the request is not cancelled, and late synchronization prevents `BootManager` from seeing
 * a "pending" or stale session during the route transition (which caused
 * redirect loops).
 */
const syncReply = (reply: FetchResult) => {
  const { data: replyData, error: replyError } = reply;
  sessionActor.send({
    type: "SERVER_REPLY",
    session: isValidSession(replyData) ? cloneSession(replyData) : null,
    error: replyError ?? null,
  });
  queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
  return reply;
};

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  // If already authenticated, redirect to the app root.
  beforeLoad: async ({ search }) => {
    // Multi-account: `?mode=add` opens /login to ADD another account while the
    // current one stays signed in (Gmail style). The "already authenticated →
    // go home" gate must be skipped here; otherwise the switcher's "Add account"
    // would bounce straight back to the app and a 2nd login could never be added.
    const isAddingAccount = (search as { mode?: string }).mode === "add";
    if (isAddingAccount) {
      return;
    }

    const pinnedId = activeTabAccountId();
    if (pinnedId !== null) {
      const view = resolveActiveAccountView(
        readAccountsStateSync(),
        pinnedId,
        sessionActor.getSnapshot().context.session,
      );
      // Guest tab → show /login (do NOT redirect). Real account → bounce home.
      if (view.isAuthenticated) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
      }
      return;
    }

    // Fast path: check machine state before hitting the network.
    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
      if (snapshot.context.session) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
      }
      return;
    }

    // Network check — only if machine has no session yet.
    // Use api.GET (not fetchApi) so we can inspect the status without throwing.
    //
    // P23/CC-60 · I1-bis: this `await` CANNOT be unbounded. A STALLED
    // `get-session` (held: neither 401 nor 5xx, which resolve quickly and also
    // `queryFn` turns them into SUCCESS with `null`) left this `beforeLoad`
    // unresolved, the router without mounting the route, and `/login` COMPLETELY
    // blank — `#root` with zero children at 3/6/10/15/20 s, measured on the
    // production bundle too. The sibling `_app` guard already had a ceiling, and
    // therefore `/` came out healthy: I1's green check measured the wrong route.
    //
    // The request is NOT cancelled: it stays alive and synchronizes machine + cache when
    // it arrives. If there turned out to be a session, `BootManager` (SESSION_RESOLVED over
    // `ready` → `checkingNavigation` → `redirecting`) takes the user out of the
    // login screen: at most a flicker, never a blank screen.
    const inFlight = (async (): Promise<FetchResult | null> => {
      try {
        return syncReply(await fetchSession());
      } catch {
        // Network error — let user try to log in anyway.
        return null;
      }
    })();

    const timedOut = new Promise<null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, SESSION_CHECK_TIMEOUT_MS);
    });

    const result = await Promise.race([inFlight, timedOut]);
    if (!result) {
      // Timeout or network error: the login is PAINTED. The late response is still
      // attached above and BootManager redirects if it eventually brings a session.
      return;
    }

    // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
    // Decide from the session the machine accepted, not from the raw reply: a
    // stale get-session that LOGOUT revoked must not bounce the guest off /login.
    if (sessionActor.getSnapshot().context.session) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
    // 401/403, network error, or null session — not authenticated, show login page.
  },
  component: AuthLayoutRoute,
});
